import type { TFunction } from 'i18next';
import { useCallback, useEffect, useState } from 'react';

import { handleApiError } from '@/lib/handleApiError';

/**
 * Loads a list (or any single value) from an async fetcher on mount, tracks
 * loading + error state, and cancels the side-effect if the component unmounts
 * before the fetch resolves.
 *
 * Used by ApproverDashboard for three independent feeds (workflows, queue,
 * recent decisions). Replaces the per-feed try/catch/finally + cancelled-flag
 * dance — each feed becomes one line.
 *
 * Returns:
 *   - data: T | null  — null while loading or after a failure
 *   - loading: boolean
 *   - setData: dispatch to patch the list in place after a mutation
 *
 * Error path: wired through handleApiError so 409 lock-conflicts get the sticky
 * modal treatment. Caller supplies the i18n key for the toast message.
 */
export function useFetchedList<T>(
  fetcher: () => Promise<T>,
  t: TFunction,
  errMessageKey: string,
  /**
   * Values that should trigger a refetch when they change. Defaults to `[]` —
   * fetch once on mount, the behaviour every caller had before this existed.
   * The Audit History page passes its server-side search + date range here.
   */
  deps: unknown[] = [],
): {
  data: T | null;
  loading: boolean;
  setData: React.Dispatch<React.SetStateAction<T | null>>;
} {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  // Stable error key — useCallback so the effect dep array doesn't re-run on every
  // render just because `t` got a new identity from useTranslation.
  const onError = useCallback(
    (err: unknown) => handleApiError(err, t, t(errMessageKey)),
    [t, errMessageKey],
  );

  useEffect(() => {
    let cancelled = false;
    // Re-entering the effect means a dep changed, so show the spinner again.
    // `data` is deliberately NOT cleared — blanking the table on every keystroke
    // makes a search box flicker; the stale rows stay until the new ones land.
    setLoading(true);
    fetcher()
      .then((value) => {
        if (!cancelled) setData(value);
      })
      .catch((err) => {
        if (!cancelled) onError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `fetcher` is intentionally excluded — callers pass an inline closure, so
    // depending on it would refetch every render. `deps` is the explicit contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, setData };
}
